import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { filterAnalysesByAnalysisType, mapAnalysisToAnalysisItem } from '@/shared/api/mappers/analysis';
import type { AnalysisType as AnalysisTypeBackend } from '@/shared/api/types/generated';
import i18n from '@/shared/i18n';
import { useGetUserAnalysesPaginatedQuery } from '@/shared/hooks/queries/generated';
import type { AnalysisType } from '@/shared/types/analysis';

export const useAnalysisMenuListData = (type: AnalysisType) => {
  // Mapper le type frontend vers le type backend
  let analysisTypeBackend: AnalysisTypeBackend;
  switch (type) {
    case 'product':
      analysisTypeBackend = 'catalog';
      break;
    case 'vendor':
      analysisTypeBackend = 'profile';
      break;
    default:
      throw new Error(`Unsupported analysis type: ${type}`);
  }

  // Utiliser l'infinite query pour la pagination
  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage, refetch } = useInfiniteQuery({
    queryKey: ['GetUserAnalysesPaginated', analysisTypeBackend],
    queryFn: ({ pageParam }) =>
      useGetUserAnalysesPaginatedQuery.fetcher({
        type: analysisTypeBackend,
        cursor: pageParam,
        limit: 100,
      })(),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.getUserAnalysesPaginated.nextCursor,
    refetchInterval: 30000,
  });

  // Enchaîne TOUTES les pages jusqu'à épuisement (2026-08-26) : le serveur
  // plafonne limit à 100, et les pages « Mes recherches » groupent par
  // dossiers et filtrent CÔTÉ CLIENT — au-delà de 100 recherches, chaque
  // création faisait « disparaître » la plus ancienne de l'affichage. Le
  // garde !isFetchingNextPage évite de relancer un chargement à chaque rendu
  // survenu pendant la requête en vol.
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Combiner toutes les pages d'analyses
  const analyses = useMemo(() => {
    const allAnalyses = data?.pages.flatMap((page) => page.getUserAnalysesPaginated.analyses) ?? [];
    // Filtrer selon le type d'analyse (au cas où nécessaire)
    const filteredAnalyses = filterAnalysesByAnalysisType(allAnalyses, type);
    // Mapper vers le format frontend
    return filteredAnalyses.map(mapAnalysisToAnalysisItem);
  }, [data, type]);

  return {
    analyses,
    loading: isLoading,
    error: error ? i18n.t('analysisMenu.loadError') : null,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};
